<template>
  <div class="my-account-settings">
    <div class="container">
      <PageHeader title="My account" description="Manage your account settings" />
      <div class="my-account">
        <div class="form-section">
          <FileInput
            id="account-avatar"
            :label="inputs.avatar_url.label"
            accept="image/*"
            v-model="inputs.avatar_url.binding"
            :upload-config="avatarUploadConfig"
            @update:modelValue="markChanged"
          />

          <TextInput
            :id="inputs.first_name.id"
            :label="inputs.first_name.label"
            v-model="inputs.first_name.binding"
            :error="inputs.first_name.error"
            :rules="inputs.first_name.rules"
            @validate="onValidate('first_name', $event)"
            @update:modelValue="markChanged"
          />

          <TextInput
            :id="inputs.last_name.id"
            :label="inputs.last_name.label"
            v-model="inputs.last_name.binding"
            :error="inputs.last_name.error"
            :rules="inputs.last_name.rules"
            @validate="onValidate('last_name', $event)"
            @update:modelValue="markChanged"
          />

          <TextInput
            :id="inputs.email.id"
            :label="inputs.email.label"
            v-model="inputs.email.binding"
            :error="inputs.email.error"
            :rules="inputs.email.rules"
            @validate="onValidate('email', $event)"
            disabled="true"
          />
          <div v-if="isGoogleUser" class="google-email-notice">
            Email cannot be changed for Google-authenticated accounts
          </div>
        </div>
        <div class="settings-footer">
          <Btn
            :text="saving ? 'Saving...' : 'Save Changes'"
            @click="saveSettings"
            :disabled="!hasChanges"
            :loading="saving"
          />
        </div>

        <!-- Password Section -->
        <div v-if="!isGoogleUser" class="form-section password-section">
          <h3 class="section-title">Password</h3>
          <p class="section-description">Update your password to keep your account secure.</p>
          <Btn text="Change Password" variant="outline" @click="openChangePasswordModal" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, watch } from 'vue';
  import axios from 'axios';
  import { useValidation } from '@/composables/useValidation';
  import TextInput from '@/components/inputs/TextInput.vue';
  import FileInput from '@/components/inputs/FileInput.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import ChangePasswordModal from '@/components/settings/ChangePasswordModal.vue';
  import { useUIStore } from '@/store/uiStore';
  import { useUserStore } from '@/store/userStore';
  import { useToastStore } from '@/store/toastStore';

  export default {
    name: 'SettingsMyAccountView',

    components: {
      PageHeader,
      Btn,
      TextInput,
      FileInput,
    },

    setup() {
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const user = computed(() => userStore.user);
      const userData = computed(() => userStore.userData);
      const saving = ref(false);
      const hasChanges = ref(false);
      const originalSettings = ref({});

      const isGoogleUser = computed(() => {
        return user.value?.app_metadata?.provider === 'google';
      });

      const avatarUploadConfig = computed(() => ({
        type: 'avatar',
        organizationId: userStore.currentOrganization?.id,
        context: 'user',
      }));

      const openChangePasswordModal = () => {
        uiStore.addModal(ChangePasswordModal, {
          title: 'Change Password',
          description: 'Enter your current password and choose a new password.',
          onSubmit: () => {
            uiStore.closeModal();
          },
        });
      };

      const { inputs, validateForm, onValidate } = useValidation({
        first_name: {
          id: 'first_name',
          label: 'Voornaam',
          binding: '',
          error: null,
          rules: ['required'],
          touched: false,
        },
        last_name: {
          id: 'last_name',
          label: 'Achternaam',
          binding: '',
          error: null,
          rules: ['required'],
          touched: false,
        },
        email: {
          id: 'email',
          label: 'Email',
          binding: '',
          error: null,
          rules: ['required', 'email'],
          touched: false,
        },
        avatar_url: {
          id: 'avatar_url',
          label: 'Profielfoto',
          binding: '',
          error: null,
          rules: [],
          touched: false,
        },
      });

      const snapshotBindings = () => ({
        first_name: inputs.value.first_name.binding,
        last_name: inputs.value.last_name.binding,
        email: inputs.value.email.binding,
        avatar_url: inputs.value.avatar_url.binding,
      });

      const initializeSettings = () => {
        const data = userData.value || {};
        const meta = user.value?.user_metadata || {};
        inputs.value.first_name.binding = data.first_name || meta.first_name || '';
        inputs.value.last_name.binding = data.last_name || meta.last_name || '';
        inputs.value.email.binding = data.email || user.value?.email || '';
        inputs.value.avatar_url.binding = data.avatar_url || meta.avatar_url || '';
        originalSettings.value = snapshotBindings();
        hasChanges.value = false;
      };

      const markChanged = () => {
        const now = snapshotBindings();
        hasChanges.value = Object.keys(now).some(
          (key) => now[key] !== originalSettings.value[key]
        );
      };

      const saveSettings = async () => {
        if (!(await validateForm())) return;
        saving.value = true;
        try {
          await axios.patch('/users/profile', {
            first_name: inputs.value.first_name.binding.trim(),
            last_name: inputs.value.last_name.binding.trim(),
            avatar_url: inputs.value.avatar_url.binding || null,
          });
          await userStore.fetchUserData();
          originalSettings.value = snapshotBindings();
          hasChanges.value = false;
          toastStore.addToast({ type: 'success', message: 'Profiel opgeslagen' });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || 'Opslaan mislukt',
          });
        } finally {
          saving.value = false;
        }
      };

      watch(userData, () => initializeSettings(), { immediate: false });

      onMounted(() => {
        initializeSettings();
      });

      return {
        user,
        inputs,
        hasChanges,
        saving,
        onValidate,
        saveSettings,
        markChanged,
        avatarUploadConfig,
        isGoogleUser,
        openChangePasswordModal,
      };
    },
  };
</script>

<style scoped lang="scss">
  .my-account-settings {
    .avatar-section {
      display: flex;
      align-items: center;
      gap: 2rem;
      margin-bottom: 2rem;

      .current-avatar {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        object-fit: cover;
      }

      .avatar-controls {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
    }

    .password-section {
      margin-top: 2rem;
      padding-top: 2rem;
      border-top: 1px solid var(--color-border, #e5e7eb);

      .section-title {
        font-size: 1.125rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: var(--color-text-primary, #111827);
      }

      .section-description {
        font-size: 0.875rem;
        color: var(--color-text-secondary, #6b7280);
        margin-bottom: 1rem;
      }
    }
  }
</style>
